import { forwardRef, useEffect, type ComponentPropsWithoutRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Button, Dialog, Input } from "@akuna/ui";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { createAdHocSaleItemSchema, type AdHocSaleItemInput } from "@/lib/sales/adHocSaleItem";

interface AdHocItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddItem: (input: AdHocSaleItemInput) => void;
}

const FORM_ID = "ad-hoc-item-form";
const MAX_CENTS_DIGITS = 10;
const centsFormatter = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface CentsInputProps extends Omit<ComponentPropsWithoutRef<typeof Input>, "value" | "onChange" | "type"> {
  value?: number;
  onValueChange: (value: number | null) => void;
}

/** Valor em reais digitado da direita para a esquerda: a vírgula das duas casas já vem fixa. */
const CentsInput = forwardRef<HTMLInputElement, CentsInputProps>(({ value, onValueChange, ...props }, ref) => (
  <div className="akuna-affix-input" data-invalid={props["aria-invalid"] === true || props["aria-invalid"] === "true" || undefined}>
    <span className="akuna-affix-input__prefix" aria-hidden="true">R$</span>
    <Input
      ref={ref}
      inputMode="numeric"
      autoComplete="off"
      className="akuna-affix-input__control"
      {...props}
      value={value === undefined ? "" : centsFormatter.format(value)}
      onChange={(event) => {
        const digits = event.target.value.replace(/\D/g, "").replace(/^0+/, "").slice(0, MAX_CENTS_DIGITS);
        onValueChange(digits ? Number(digits) / 100 : null);
      }}
    />
  </div>
));
CentsInput.displayName = "CentsInput";
const defaultValues = { name: "", quantity: 1, unitPrice: "" };
type FormValues = { name: string; quantity: number | string; unitPrice: number | string };

export function AdHocItemDialog({ open, onOpenChange, onAddItem }: AdHocItemDialogProps) {
  const { t, i18n } = useTranslation("erp");
  const form = useForm<FormValues>({
    resolver: zodResolver(createAdHocSaleItemSchema(t)),
    defaultValues,
  });

  useEffect(() => {
    if (open) form.reset(defaultValues);
  }, [open, form]);

  const quantity = Number(form.watch("quantity"));
  const unitPrice = Number(form.watch("unitPrice"));
  const total = quantity * unitPrice;
  const formattedTotal = new Intl.NumberFormat(i18n.language, { style: "currency", currency: "BRL" })
    .format(Number.isFinite(total) && quantity > 0 && unitPrice > 0 ? total : 0);

  const onSubmit = (values: FormValues) => {
    const { name, quantity, unitPrice } = createAdHocSaleItemSchema(t).parse(values);
    onAddItem({ name, quantity, unitPrice });
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      mode="adaptive"
      className="sm:max-w-md"
      title={t("adHocItem.title")}
      closeLabel={t("close", { ns: "common" })}
      footer={
        <div className="flex w-full items-center gap-3">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
          <Button type="submit" form={FORM_ID} className="ms-auto">{t("adHocItem.add")}</Button>
        </div>
      }
    >
      <Form {...form}>
        <form id={FORM_ID} onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-4">
          <FormField control={form.control} name="name" render={({ field }) => (
            <FormItem>
              <FormLabel>{t("adHocItem.name")}</FormLabel>
              <FormControl>
                <Input placeholder={t("adHocItem.namePlaceholder")} autoComplete="off" data-1p-ignore {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )} />
          <div className="grid grid-cols-2 gap-4">
            <FormField control={form.control} name="quantity" render={({ field }) => (
              <FormItem>
                <FormLabel>{t("adHocItem.quantity")}</FormLabel>
                <FormControl><Input type="number" inputMode="numeric" min="1" step="1" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="unitPrice" render={({ field }) => (
              <FormItem>
                <FormLabel>{t("adHocItem.unitPrice")}</FormLabel>
                <FormControl>
                  <CentsInput
                    ref={field.ref}
                    name={field.name}
                    placeholder="0,00"
                    value={typeof field.value === "number" ? field.value : undefined}
                    onValueChange={(value) => field.onChange(value ?? "")}
                    onBlur={field.onBlur}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />
          </div>
          <div className="flex items-center justify-between rounded-md bg-muted p-3" role="status">
            <span>{t("adHocItem.total")}</span>
            <span className="font-semibold">{formattedTotal}</span>
          </div>
          <p className="text-sm text-muted-foreground">{t("adHocItem.fiscalHint")}</p>
        </form>
      </Form>
    </Dialog>
  );
}
